import { useTranslation } from "react-i18next";
import styles from "./style.module.scss";
import { useEffect, useState } from "react";
import { getOrganizationDocumentations } from "../../../../../setup/api/services/organization";
import { IOrganizations } from "../../../../../setup/types/Organisation";
import Spinner from "../../../../../components/common/Spinner";
import { IOrganizationDocumentation } from "../../../../../setup/api/services/organization/models";
import PDFComponent from "../../../../../components/common/PDFComponent";
import DenyAccess from "../../../balance/denyAccess";

// Helper function to determine if a file is an image based on its URL or extension
const isImageFile = (url: string): boolean => {
  if (!url) return false;
  // Check for common image file extensions
  const imageExtensions = [
    ".jpg",
    ".jpeg",
    ".png",
    ".gif",
    ".bmp",
    ".webp",
    ".svg",
  ];
  const urlLower = url.toLowerCase();
  // Check if the URL ends with any image extension
  return imageExtensions.some((ext) => urlLower.includes(ext));
};

interface DocumentationInformationProps {
  organization: IOrganizations | undefined;
}

const DocumentationInformation = ({
  organization,
}: DocumentationInformationProps) => {
  const { t } = useTranslation();
  const [documentations, setDocumentations] = useState<
    IOrganizationDocumentation[]
  >([]);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!organization) return;
    const fetchDocumentations = async () => {
      setIsLoading(true);
      try {
        const response = await getOrganizationDocumentations(organization.code);
        setDocumentations(response.data);
      } catch (error: any) {
        setError(error?.error?.message);
        console.error("Error fetching documentation information:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDocumentations();
  }, [organization]);

  if (isLoading) {
    return (
      <div className={styles.spinnerContainer}>
        <Spinner />
      </div>
    );
  }

  if (error === "Access is denied") {
    return <DenyAccess />;
  }

  return (
    <div className={styles.container}>
      <div className={styles.headerContainer}>
        <h3 className={styles.title}>{t("lbl_documentation_info_title")}</h3>
      </div>
      <div className={styles.contentContainer}>
        {documentations.map((doc) => (
          <div key={doc.id} className={styles.documentationItem}>
            <p className={styles.documentationTitle}>{doc.title}</p>
            {isImageFile(doc.attachmentUrl) ? (
              <div className={styles.imageContainer}>
                <img
                  src={doc.attachmentUrl}
                  alt={doc.title}
                  className={styles.documentImage}
                  onClick={() =>
                    window.open(
                      doc.attachmentUrl,
                      "_blank",
                      "rel=noopener noreferrer"
                    )
                  }
                />
                <a
                  target="_blank"
                  download={doc.title || doc.attachmentUrl?.split("/")?.pop()}
                  href={doc.attachmentUrl}
                  rel="noreferrer"
                  className={styles.downloadLink}>
                  <img alt="download" src={"/assets/images/svg/download.svg"} />
                </a>
              </div>
            ) : (
              <PDFComponent
                key={doc.id}
                isHorizontalPDF
                fileUrl={doc.attachmentUrl}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default DocumentationInformation;
